import React, { useState, useEffect } from "react";
import config from "../../config";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Collapse,
  Grid,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useRoomNames } from './useRoomNames';
const companyData = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = companyData?.currency ?? "";
/* ================= ROW ================= */
function BookingRow({ b, onCheckIn, onInvoice, onAddPayment, roomLabel }) {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentType, setPaymentType] = useState("cash");
  const [amount, setAmount] = useState("");
  const [cardType, setCardType] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [refNo, setRefNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmitPayment = async () => {
    setError("");

    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }
    if (amt > b.pendingAmount) {
      setError(`Amount cannot exceed pending amount: ${Companycurrency}${b.pendingAmount}`);
      return;
    }

    if (paymentType === "card" && (!cardType.trim() || !cardNumber.trim())) {
      setError("Card Type and Card Number are required");
      return;
    }
    if (paymentType === "online" && !refNo.trim()) {
      setError("Reference/Transaction ID is required for online payment");
      return;
    }

const paymentData = {
  amount: amt,
  paymentType: paymentType,
};

if (paymentType === "card") {
  paymentData.cardType = cardType.trim();
  paymentData.cardNumber = cardNumber.trim();
} else if (paymentType === "online") {
  paymentData.refNo = refNo.trim();
}
    setLoading(true);
    await onAddPayment(b.bookingId, paymentData);
    setLoading(false);

    // Reset on success
    setModalOpen(false);
    setAmount("");
    setPaymentType("cash");
    setCardType("");
    setCardNumber("");
    setRefNo("");
  };

  return (
    <>
      {/* ================= SUMMARY ROW ================= */}
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: 140 }}>{b.bookingId}</TableCell>
        <TableCell sx={{ width: 220 }}>
          <Typography fontWeight={700}>
            {b.firstName} {b.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {b.mobileNumber}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          {new Date(b.checkIn).toLocaleDateString("en-IN")} →{" "}
          {new Date(b.checkOut).toLocaleDateString("en-IN")}
        </TableCell>
        <TableCell sx={{ width: 120 }}>
          {b.isCancel ? (
            <Chip label="Cancelled" color="error" size="small" />
          ) : b.ischeckin ? (
            <Chip label="Checked-In" color="success" size="small" />
          ) : (
            <Chip label="Booked" color="warning" size="small" />
          )}
        </TableCell>
        <TableCell sx={{ width: 120 }}>
          <Typography
            fontWeight={700}
            color={b.pendingAmount > 0 ? "error.main" : "success.main"}
          >
            {Companycurrency} {b.pendingAmount}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 220 }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            {/* Check-In only if no pending amount */}
            {!b.ischeckin && !b.isCancel && b.pendingAmount === 0 && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => onCheckIn(b.bookingId)}
              >
                Check-In
              </Button>
            )}
            {/* Add Payment if pending > 0 */}
            {!b.ischeckin && !b.isCancel && b.pendingAmount > 0 && (
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => setModalOpen(true)}
              >
                Add Payment
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => onInvoice(b)}
            >
              Invoice
            </Button>
          </Box>
        </TableCell>
      </TableRow>

      {/* ================= DETAILS ROW ================= */}
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box p={2} bgcolor="#f5f7fa">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: "100%" }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">Hotel</Typography>
                    <Typography fontWeight={700}>{b.hotelName}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">Rooms</Typography>
                    <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                      {b.roomNumbers ? String(b.roomNumbers).split(',').map((n) => roomLabel(n.trim())).join(', ') : b.roomNumbers}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{b.roomType}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">Booking Source</Typography>
                    <Typography fontWeight={600}>{b.through}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: "100%", bgcolor: "#fff8e1" }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">Amount Summary</Typography>
                    <Box mt={1}>
                      <Row label="Base Price" value={`${Companycurrency} ${b.price}`} />
                      <Row label="Total Price" value={`${Companycurrency} ${b.totelPrice}`} />
                      <Row label="Final (GST)" value={`${Companycurrency} ${b.finalpriceWithGST}`} bold />
                      <Divider sx={{ my: 1 }} />
                      <Row
                        label="Pending"
                        value={`${Companycurrency} ${b.pendingAmount}`}
                        color={b.pendingAmount > 0 ? "error.main" : "success.main"}
                        bold
                      />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: "100%" }} elevation={1}>
                    <Typography variant="subtitle2" color="text.secondary">Guest & Verification</Typography>
                    <Typography fontWeight={700}>{b.firstName} {b.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">{b.mobileNumber}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">ID Proof</Typography>
                    <Typography fontWeight={600}>{b.idType} : {b.idNumber}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                    <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                      {b.address}, {b.city}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">Invoice Generated</Typography>
                    <Typography fontWeight={600}>{b.isinvoice ? "Yes" : "No"}</Typography>
                    {b.isCancel && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="error.main">Cancel Remark</Typography>
                        <Typography color="error.main" fontWeight={600}>{b.cancelRemark}</Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* ================= ADD PAYMENT MODAL ================= */}
      <Dialog open={modalOpen} onClose={() => !loading && setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Payment - Pending: ${Companycurrency}{b.pendingAmount}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              disabled={loading}
              inputProps={{ min: 1, max: b.pendingAmount }}
              helperText={`Maximum allowed: ${Companycurrency}${b.pendingAmount}`}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentType}
                label="Payment Type"
                onChange={(e) => setPaymentType(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="online">Online</MenuItem>
              </Select>
            </FormControl>

            {paymentType === "card" && (
              <>
                <TextField
                  label="Card Type (Visa, MasterCard, etc.)"
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                  fullWidth
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Card Number (Last 4 digits or full)"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  fullWidth
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
              </>
            )}

            {paymentType === "online" && (
              <TextField
                label="Reference / Transaction ID"
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                fullWidth
                disabled={loading}
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Submit Payment"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ===== Helper Row ===== */
const Row = ({ label, value, bold, color }) => (
  <Box display="flex" justifyContent="space-between" mb={0.5}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography fontWeight={bold ? 700 : 600} color={color || "text.primary"}>
      {value}
    </Typography>
  </Box>
);

/* ================= MAIN PAGE ================= */
export default function BulkBookings() {
  const { roomLabel } = useRoomNames();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const rowsPerPage = 30;

  const API = {
    LIST: `${config.API_BASE_URL}/Home/Getallbulkbookings`,
    CHECKIN: `${config.API_BASE_URL}/Home/CheckInBooking`,
    INVOICE: `${config.API_BASE_URL}/Home/GenerateInvoice`,
    ADD_PAYMENT: `${config.API_BASE_URL}/Home/AddbulkPayment`,
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const token = sessionStorage.getItem("token");

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await fetch(API.LIST, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch bookings");
      const d = await response.json();
      setBookings(Array.isArray(d) ? d : []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setSnackbar({ open: true, message: "Failed to load bookings", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const filtered = bookings.filter(
    (b) =>
      `${b.firstName} ${b.lastName}`.toLowerCase().includes(searchText.toLowerCase()) ||
      (b.mobileNumber || "").includes(searchText)
  );

  const handleCheckIn = async (id) => {
    try {
      const res = await fetch(`${API.CHECKIN}?bookingId=${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSnackbar({ open: true, message: "Checked-in successfully", severity: "success" });
        fetchBookings();
      } else {
        setSnackbar({ open: true, message: "Check-in failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Network error", severity: "error" });
    }
  };

  const handleInvoice = async (b) => {
    try {
      const res = await fetch(API.INVOICE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ BookingId: b.bookingId }),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: "Invoice generated", severity: "success" });
        fetchBookings();
      } else {
        setSnackbar({ open: true, message: "Invoice generation failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Network error", severity: "error" });
    }
  };

const handleAddPayment = async (bookingId, paymentData) => {
  try {
    // Build body dynamically - send only the fields that exist
    const body = {
      BookingId: bookingId,
      Amount: paymentData.amount,
      PaymentType: paymentData.paymentType,
    };

    // Only add if they exist
    if (paymentData.cardType !== undefined) {
      body.CardType = paymentData.cardType;
    }
    if (paymentData.cardNumber !== undefined) {
      body.CardNumber = paymentData.cardNumber;
    }
    if (paymentData.refNo !== undefined) {
      body.RefNo = paymentData.refNo;
    }

    console.log("Sending payload:", body); // ← to match with the Network tab

    const res = await fetch(API.ADD_PAYMENT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (res.ok) {
      setSnackbar({ open: true, message: "Payment added successfully!", severity: "success" });
      fetchBookings();
    } else {
      const errMsg = await res.text();
      console.error("Server error:", errMsg);
      setSnackbar({
        open: true,
        message: `Payment failed: ${errMsg || "Check data"}`,
        severity: "error",
      });
    }
  } catch (err) {
    console.error("Network error:", err);
    setSnackbar({ open: true, message: "Network error", severity: "error" });
  }
};

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" align="center" mb={3} fontWeight={700}>
        Bulk Bookings
      </Typography>

      <TextField
        label="Search by name or mobile"
        size="small"
        fullWidth
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f0f0f0" }}>
              <TableRow>
                <TableCell />
                <TableCell>Booking ID</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Stay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((b) => (
                  <BookingRow
                    key={b.bookingId}
                    b={b}
                    onCheckIn={handleCheckIn}
                    onInvoice={handleInvoice}
                    onAddPayment={handleAddPayment}
                    roomLabel={roomLabel}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPageOptions={[30]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}